import React from 'react';
import { Paper, Box, Stack, Typography, Alert } from '@mui/material';
import { Check } from 'lucide-react';

interface BrowserSettingItem {
  label: string;
  description: string;
  checked: boolean;
  required: boolean;
}

const items: BrowserSettingItem[] = [
  {
    label: 'Enable JavaScript',
    description: 'Essential for interactive assessment elements',
    checked: true,
    required: true,
  },
  {
    label: 'Disable pop-up blockers',
    description: 'Required for opening examination windows',
    checked: true,
    required: true,
  },
  {
    label: 'Allow third-party cookies',
    description: 'Used for session validation and security',
    checked: true,
    required: false,
  },
  {
    label: 'Microphone & Camera Access',
    description: 'Only for proctored sections of the test',
    checked: true,
    required: true,
  },
];

export const BrowserSettingsCard: React.FC = () => {
  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
        textAlign: 'left',
      }}
    >
      <Box sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
          Configuration Required
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2, lineHeight: 1.4 }}>
          To ensure the assessment platform runs smoothly and records your
          progress accurately, please verify the following settings in your
          browser.
        </Typography>

        {/* Checklist */}
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {items.map((item, index) => (
            <Stack
              key={index}
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: index < items.length - 1 ? '1px solid' : 'none',
                borderBottomColor: 'divider',
              }}
            >
              {/* Checkbox indicator */}
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: 1,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid',
                  bgcolor: item.checked ? 'secondary.main' : 'background.paper',
                  borderColor: item.checked ? 'secondary.main' : 'grey.400',
                  transition: 'all 150ms ease',
                }}
              >
                {item.checked && <Check size={12} color="#ffffff" strokeWidth={3} />}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontSize: 10, color: 'text.secondary', mt: 0.25, display: 'block', lineHeight: 1.2 }}
                >
                  {item.description}
                </Typography>
              </Box>

              {item.required && !item.checked && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 9,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: 'error.main',
                    letterSpacing: 0.5,
                    flexShrink: 0,
                  }}
                >
                  Required
                </Typography>
              )}
            </Stack>
          ))}
        </Paper>

        {/* VPN note */}
        <Alert
          severity="warning"
          sx={{
            mt: 2,
            borderRadius: 2,
            fontSize: 11,
            py: 0.5,
            px: 1.5,
            alignItems: 'center',
          }}
        >
          If you are using a corporate VPN or restricted network, please ensure these domains are whitelisted.
        </Alert>
      </Box>
    </Paper>
  );
};

